import { z } from 'zod';
import {
  AssistantError,
  calendarWorkingHours,
  timestampSchema,
  type AssistantConfiguration,
  type BusinessAction,
} from './assistant';
import type { CalendarSlot } from './appointment-contracts';

const rangeSchema = z
  .object({ startsAt: timestampSchema, endsAt: timestampSchema })
  .strict()
  .refine((range) => {
    const duration = Date.parse(range.endsAt) - Date.parse(range.startsAt);
    return duration > 0 && duration <= 14 * 86_400_000;
  }, 'Calendar range must be positive and no longer than 14 days.');

export const availableCalendarSlots = (
  requestedRange: CalendarSlot,
  settings: NonNullable<AssistantConfiguration['calendar']>,
  busy: readonly CalendarSlot[],
  now: Date,
  options: { accept?: (startsAt: number) => boolean; maxResults?: number } = {}
): CalendarSlot[] => {
  const range = rangeSchema.parse(requestedRange);
  if (!Number.isFinite(now.getTime()))
    throw new AssistantError('APPOINTMENT_CLOCK_INVALID', 500);
  if (
    !Number.isInteger(settings.durationMinutes) ||
    settings.durationMinutes < 5 ||
    settings.durationMinutes > 240
  )
    throw new AssistantError('CALENDAR_DURATION_INVALID', 409);
  const maxResults = options.maxResults ?? 100;
  if (!Number.isInteger(maxResults) || maxResults < 1)
    throw new AssistantError('CALENDAR_PAGE_SIZE_INVALID', 422);
  const periods = busy.map((period) => {
    const start = Date.parse(timestampSchema.parse(period.startsAt));
    const end = Date.parse(timestampSchema.parse(period.endsAt));
    if (start >= end)
      throw new AssistantError('CALENDAR_BUSY_RANGE_INVALID', 502);
    return { start, end };
  });
  const format = new Intl.DateTimeFormat('en-US', {
    timeZone: settings.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const minuteOfDay = (value: string): number =>
    Number(value.slice(0, 2)) * 60 + Number(value.slice(3));
  const weeklyHours = calendarWorkingHours(settings);
  const earliest = Math.max(Date.parse(range.startsAt), now.getTime() + 1);
  const end = Date.parse(range.endsAt);
  const slots: CalendarSlot[] = [];
  for (
    let start = Math.ceil(earliest / 60_000) * 60_000;
    start + settings.durationMinutes * 60_000 <= end;
    start += 60_000
  ) {
    const parts = new Map(
      format.formatToParts(start).map((part) => [part.type, part.value])
    );
    const weekday = weekdays.indexOf(parts.get('weekday') ?? '');
    const minute = Number(parts.get('hour')) * 60 + Number(parts.get('minute'));
    const hours = weeklyHours.find(
      (entry) =>
        entry.day === weekday &&
        minute >= minuteOfDay(entry.opensAt) &&
        minute < minuteOfDay(entry.closesAt)
    );
    if (!hours) continue;
    const opens = minuteOfDay(hours.opensAt);
    const closes = minuteOfDay(hours.closesAt);
    if (
      minute < opens ||
      minute >= closes ||
      (minute - opens) % settings.durationMinutes !== 0
    )
      continue;
    const finish = start + settings.durationMinutes * 60_000;
    const endParts = new Map(
      format.formatToParts(finish).map((part) => [part.type, part.value])
    );
    const endMinute =
      Number(endParts.get('hour')) * 60 + Number(endParts.get('minute'));
    if (
      endParts.get('day') !== parts.get('day') ||
      endParts.get('month') !== parts.get('month') ||
      endParts.get('year') !== parts.get('year') ||
      endMinute < opens ||
      endMinute > closes
    )
      continue;
    // A DST transition can cross a closed part of the local working window.
    if (endMinute - minute !== settings.durationMinutes) {
      let withinHours = true;
      for (let instant = start + 60_000; instant < finish; instant += 60_000) {
        const local = new Map(
          format.formatToParts(instant).map((part) => [part.type, part.value])
        );
        const localMinute =
          Number(local.get('hour')) * 60 + Number(local.get('minute'));
        if (localMinute < opens || localMinute >= closes) {
          withinHours = false;
          break;
        }
      }
      if (!withinHours) continue;
    }
    if (periods.some((period) => start < period.end && finish > period.start))
      continue;
    if (options.accept && !options.accept(start)) continue;
    slots.push({
      startsAt: new Date(start).toISOString(),
      endsAt: new Date(finish).toISOString(),
    });
    if (slots.length === maxResults) break;
  }
  return slots;
};

type SlotPreference = Extract<
  BusinessAction,
  { kind: 'find_appointment_slots' }
>;

export function appointmentSearchRange(
  preference: SlotPreference,
  now: Date
): CalendarSlot {
  const dayMs = 86_400_000;
  if (preference.localDate) {
    const day = Date.parse(`${preference.localDate}T00:00:00Z`);
    if (day - now.getTime() > 60 * dayMs)
      throw new AssistantError('APPOINTMENT_DATE_OUT_OF_RANGE', 422);
    return {
      startsAt: new Date(day - dayMs).toISOString(),
      endsAt: new Date(day + 2 * dayMs).toISOString(),
    };
  }
  return {
    startsAt: now.toISOString(),
    endsAt: new Date(now.getTime() + 14 * dayMs).toISOString(),
  };
}

export function findAvailableAppointmentSlots(
  preference: SlotPreference,
  range: CalendarSlot,
  settings: NonNullable<AssistantConfiguration['calendar']>,
  busy: readonly CalendarSlot[],
  now: Date,
  accept: (startsAt: number) => boolean = () => true
): {
  slots: CalendarSlot[];
  hasMore: boolean;
  nextOffset: number | null;
  requestedTimeAvailable: boolean | null;
  nearestAlternatives: CalendarSlot[];
} {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: settings.timezone,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const wanted = preference.localDate;
  const nowParts = Object.fromEntries(
    formatter.formatToParts(now).map((part) => [part.type, part.value])
  );
  const nowLocalDate = `${nowParts['year']}-${nowParts['month']}-${nowParts['day']}`;
  const nowWeekday = weekdays.indexOf(nowParts['weekday'] ?? '');
  const nextWeekdayDate =
    preference.weekday === null
      ? null
      : (() => {
          let daysAhead = (preference.weekday - nowWeekday + 7) % 7;
          const localNowTime = `${nowParts['hour']}:${nowParts['minute']}`;
          if (
            daysAhead === 0 &&
            preference.localTime &&
            preference.localTime <= localNowTime
          )
            daysAhead = 7;
          return new Date(
            Date.parse(`${nowLocalDate}T00:00:00Z`) + daysAhead * 86_400_000
          )
            .toISOString()
            .slice(0, 10);
        })();
  const matches = (startsAt: number, exactTime: boolean) => {
    const parts = Object.fromEntries(
      formatter.formatToParts(startsAt).map((part) => [part.type, part.value])
    );
    const localDate = `${parts['year']}-${parts['month']}-${parts['day']}`;
    const hour = Number(parts['hour']);
    const localTime = `${parts['hour']}:${parts['minute']}`;
    return (
      (!wanted || localDate === wanted) &&
      (nextWeekdayDate === null || localDate === nextWeekdayDate) &&
      (!preference.timeWindow ||
        (localTime >= preference.timeWindow.startsAt &&
          Number(parts['hour']) * 60 +
            Number(parts['minute']) +
            settings.durationMinutes <=
            Number(preference.timeWindow.endsAt.slice(0, 2)) * 60 +
              Number(preference.timeWindow.endsAt.slice(3)))) &&
      (!exactTime ||
        !preference.localTime ||
        localTime === preference.localTime) &&
      (preference.timeOfDay === 'any' ||
        (preference.timeOfDay === 'morning' ? hour < 12 : hour >= 12))
    );
  };
  // A normal appointment day fits in one read. Cap short-service calendars at
  // 100 slots so their evidence stays inside the SourceFact content limit.
  const pageSize = wanted || nextWeekdayDate ? 100 : 5;
  let available = availableCalendarSlots(range, settings, busy, now, {
    maxResults: preference.offset + pageSize + 1,
    accept: (startsAt) => matches(startsAt, true) && accept(startsAt),
  });
  const requestedTimeAvailable = preference.localTime
    ? available.length > 0
    : null;
  let nearestAlternatives: CalendarSlot[] = [];
  if (requestedTimeAvailable === false && (wanted || nextWeekdayDate)) {
    const [hours, minutes] = preference.localTime!.split(':').map(Number);
    const desiredMinutes = hours! * 60 + minutes!;
    const minutesFromRequested = (slot: CalendarSlot) => {
      const parts = Object.fromEntries(
        formatter
          .formatToParts(new Date(slot.startsAt))
          .map((part) => [part.type, part.value])
      );
      return (
        Number(parts['hour']) * 60 + Number(parts['minute']) - desiredMinutes
      );
    };
    // Only real alternatives on the requested local day; never silently move a week.
    available = availableCalendarSlots(range, settings, busy, now, {
      maxResults: Math.ceil((25 * 60) / settings.durationMinutes),
      accept: (startsAt) => matches(startsAt, false) && accept(startsAt),
    }).sort(
      (a, b) =>
        Math.abs(minutesFromRequested(a)) - Math.abs(minutesFromRequested(b)) ||
        Date.parse(a.startsAt) - Date.parse(b.startsAt)
    );
    // Give the buyer a choice on each side of their time when both are available.
    const earlier = available.find((slot) => minutesFromRequested(slot) < 0);
    const later = available.find((slot) => minutesFromRequested(slot) > 0);
    nearestAlternatives =
      earlier && later ? [earlier, later] : available.slice(0, 2);
  }
  const offset = preference.offset;
  const selected = available.slice(offset, offset + pageSize);
  const hasMore = available.length > offset + pageSize;
  return {
    slots: selected,
    hasMore,
    nextOffset: hasMore ? offset + selected.length : null,
    requestedTimeAvailable,
    nearestAlternatives,
  };
}
